import { useAuthStore } from "@/stores/authStore";
import { getOrCreateDeviceId } from "@medicorepay/shared/lib/telemetry-sdk";
import {
  parseStableApiError,
  StableApiErrorResponse,
} from "@medicorepay/shared";
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

const ADMIN_API_PREFIX = "/api/v1/admin";

function normalizeAdminApiUrl(rawUrl?: string): string {
  const value = (rawUrl || "").trim();
  if (!value) {
    return ADMIN_API_PREFIX;
  }

  const normalized = value.replace(/\/+$/, "");

  if (/\/api\/v1\/admin$/i.test(normalized)) {
    return normalized;
  }

  if (/\/api\/admin$/i.test(normalized)) {
    return normalized.replace(/\/api\/admin$/i, ADMIN_API_PREFIX);
  }

  if (/\/api\/v1$/i.test(normalized)) {
    return `${normalized}/admin`;
  }

  if (/\/api$/i.test(normalized)) {
    return `${normalized}/v1/admin`;
  }

  return `${normalized}${ADMIN_API_PREFIX}`;
}

const API_URL = normalizeAdminApiUrl(
  import.meta.env.VITE_ADMIN_API_URL || import.meta.env.VITE_API_URL,
);
const SOURCE_DEVICE = getOrCreateDeviceId("admin");
const CLIENT_VERSION = import.meta.env.VITE_APP_VERSION || "1.0.0";
const ADMIN_CLIENT_HEADERS = {
  "X-Client-App": "admin",
  "X-Client-Version": CLIENT_VERSION,
  "X-Source-Device": SOURCE_DEVICE,
};

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: ADMIN_CLIENT_HEADERS,
  withCredentials: true,
});

// ═══════════════════════════════════════════════════════════════════════════
// TOKEN REFRESH QUEUE - Prevents race conditions when multiple 401s occur
// ═══════════════════════════════════════════════════════════════════════════

interface QueueItem {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}

let isRefreshing = false;
let failedQueue: QueueItem[] = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Extended request config with retry flag
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _stepUpRetry?: boolean;
}

type StableAdminAxiosError = AxiosError & {
  stableError?: StableApiErrorResponse;
};

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers["X-Source-Device"] = SOURCE_DEVICE;
  const method = config.method?.toUpperCase();
  if (
    method &&
    ["POST", "PUT", "PATCH", "DELETE"].includes(method) &&
    !config.headers["Idempotency-Key"]
  ) {
    config.headers["Idempotency-Key"] = crypto.randomUUID();
  }
  return config;
});

// Response interceptor for error handling with token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: StableAdminAxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // Extract error message from API response
    const responseData = error.response?.data as
      | Record<string, unknown>
      | undefined;
    if (responseData?.message) {
      error.message = responseData.message as string;
    }
    const stableError = parseStableApiError(responseData);
    if (stableError) error.stableError = stableError;

    if (
      error.response?.status === 403 &&
      responseData?.code === "PLATFORM_STEP_UP_REQUIRED" &&
      originalRequest &&
      !originalRequest._stepUpRetry
    ) {
      originalRequest._stepUpRetry = true;
      const password = window.prompt(
        "This sensitive action requires password verification. Enter your admin password:",
      );
      if (!password) return Promise.reject(error);
      const code = useAuthStore.getState().user?.mfaEnabled
        ? window.prompt("Enter your authenticator or recovery code:")
        : undefined;
      if (useAuthStore.getState().user?.mfaEnabled && !code) {
        return Promise.reject(error);
      }
      const token = useAuthStore.getState().token;
      await axios.post(
        `${API_URL}/auth/step-up`,
        { password, code },
        {
          withCredentials: true,
          headers: {
            ...ADMIN_CLIENT_HEADERS,
            Authorization: token ? `Bearer ${token}` : undefined,
            "Idempotency-Key": crypto.randomUUID(),
          },
        },
      );
      return apiClient(originalRequest);
    }

    // Skip token refresh for auth endpoints (login, etc.)
    const isAuthEndpoint = originalRequest?.url?.startsWith("/auth/");

    // Handle 401 Unauthorized with token refresh
    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      !isAuthEndpoint
    ) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return apiClient(originalRequest);
            }
            return Promise.reject(new Error("No original request"));
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      // Mark as refreshing and set retry flag
      if (originalRequest) {
        originalRequest._retry = true;
      }
      isRefreshing = true;

      try {
        // Rotate the secure HttpOnly platform session cookie.
        const response = await axios.post(
          `${API_URL}/auth/refresh`,
          {},
          {
            withCredentials: true,
            headers: {
              ...ADMIN_CLIENT_HEADERS,
              "Idempotency-Key": crypto.randomUUID(),
            },
          },
        );

        const { accessToken, user } = response.data;

        // Update store with new tokens
        useAuthStore.getState().setSession(accessToken, user);

        // Process queued requests with new token
        processQueue(null, accessToken);

        // Retry original request with new token
        if (originalRequest) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Process queue with error
        processQueue(refreshError as Error, null);

        // Refresh failed, logout user
        useAuthStore.getState().logout();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
